/**
 * "Recently worked on" – the reviews the owner acted on (a finish or a draft
 * comment), on top of both inboxes. Pure helpers for where each one stands.
 */
import type { AdminReviewDoc, ReviewStatus } from "../api/admin";
import type { PillTone } from "../components/Pill";
import { emailOutcome, serverTime } from "./emailDrafts";

/** The author resubmitted after the owner's last action – the review is back
 * for review and he can finish what he started. */
export function backForReview(doc: AdminReviewDoc) {
  const worked = serverTime(doc.owner_worked_at);
  return doc.status === "pending" && worked > 0 && serverTime(doc.submitted_at) > worked;
}

/** Nothing left to happen: closed, rejected, withdrawn, or an email the
 * server already sent. An approved review is still with its author. */
export function isFinished(doc: Pick<AdminReviewDoc, "status" | "sent_directly">) {
  return doc.status === "done" || doc.status === "rejected" || doc.status === "withdrawn" || !!doc.sent_directly;
}

/** Where a "Recently worked on" review stands now, as a status word. */
export function recentStatus(doc: AdminReviewDoc): { label: string; tone: PillTone } {
  if (backForReview(doc)) return { label: "back for review", tone: "signal" };
  const outcome = emailOutcome(doc);
  if (outcome) return { label: outcome, tone: "ok" };
  const words: Record<ReviewStatus, { label: string; tone: PillTone }> = {
    pending: { label: "pending", tone: "signal" },
    changes_requested: { label: "changes requested", tone: "muted" },
    approved: { label: "approved", tone: "ok" },
    done: { label: "done", tone: "ok" },
    rejected: { label: "rejected", tone: "muted" },
    withdrawn: { label: "withdrawn", tone: "muted" },
  };
  return words[doc.status];
}
